import { useEffect, useState } from 'react';
import { useCharacterStore } from '@/state/characterStore';
import { useUIStore } from '@/state/uiStore';
import { useAuthStore } from '@/state/authStore';
import { useNotificationStore } from '@/state/notificationStore';
import { disconnectRealtime } from '@/network/realtime';
import { Admin } from '@/network/RestApi';
import { useAdminStore } from '@/state/adminStore';

type PanelKey = 'inventory' | 'skill' | 'quest' | 'mail' | 'shop' | 'guild' | 'stat' | 'teleport' | 'pet' | 'friend' | 'party';

type ActionBtn = {
  key: PanelKey;
  hot: string;
  label: string;
  icon: string;
};

const ICON_BASE = '/assets/ui/icons/';

const BUTTONS: ActionBtn[] = [
  { key: 'stat',      hot: 'C', label: 'Nhân vật',  icon: `${ICON_BASE}icon_stat.png` },
  { key: 'inventory', hot: 'I', label: 'Túi đồ',    icon: `${ICON_BASE}icon_inventory.png` },
  { key: 'skill',     hot: 'K', label: 'Kỹ năng',   icon: `${ICON_BASE}icon_skill.png` },
  { key: 'quest',     hot: 'L', label: 'Nhiệm vụ',  icon: `${ICON_BASE}icon_quest.png` },
  { key: 'mail',      hot: 'M', label: 'Thư',       icon: `${ICON_BASE}icon_mail.png` },
  { key: 'shop',      hot: 'P', label: 'Shop',      icon: `${ICON_BASE}icon_shop.png` },
  { key: 'guild',     hot: 'G', label: 'Guild',     icon: `${ICON_BASE}icon_guild.png` },
  { key: 'party',     hot: 'Y', label: 'Party',     icon: `${ICON_BASE}icon_party.png` },
  { key: 'friend',    hot: 'F', label: 'Bạn bè',    icon: `${ICON_BASE}icon_friend.png` },
  { key: 'pet',       hot: 'O', label: 'Pet',       icon: `${ICON_BASE}icon_pet.png` },
  { key: 'teleport',  hot: 'B', label: 'Teleport',  icon: `${ICON_BASE}icon_teleport.png` },
];

const ICON_BTN: React.CSSProperties = {
  position: 'relative',
  width: 72,
  height: 'auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'none',
  border: 'none',
  color: '#ffd166',
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: 15,
  padding: '3px 0',
  lineHeight: 1,
  gap: 3,
};

const NAME_LABEL: React.CSSProperties = {
  fontSize: 13,
  color: '#c8daf0',
  fontWeight: 600,
  textShadow: '0 1px 2px #000',
  whiteSpace: 'nowrap',
  lineHeight: 1,
};

const BADGE: React.CSSProperties = {
  position: 'absolute',
  top: -5,
  left: -5,
  background: '#ff3b3b',
  color: '#fff',
  borderRadius: '50%',
  fontSize: 10,
  fontWeight: 700,
  minWidth: 16,
  height: 16,
  lineHeight: '16px',
  textAlign: 'center',
  padding: '0 3px',
  border: '1px solid #fff',
};

export function ActionBar() {
  const toggle = useUIStore((s) => s.toggle);
  const statPoints = useCharacterStore((s) => s.statPoints);
  const skillPoints = useCharacterStore((s) => s.skillPoints);
  const friendBadge = useNotificationStore((s) => s.friendRequests);
  const partyBadge = useNotificationStore((s) => s.partyInvites);
  const clearAuth = useAuthStore((s) => s.clear);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    Admin.me()
      .then((r) => {
        const v = !!r?.isAdmin;
        setIsAdmin(v);
        useAdminStore.getState().setIsAdmin(v);
      })
      .catch(() => {
        setIsAdmin(false);
        useAdminStore.getState().setIsAdmin(false);
      });
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.code === 'KeyB') {
        e.preventDefault();
        useUIStore.getState().toggle('teleport');
      } else if (e.code === 'KeyF') {
        e.preventDefault();
        useUIStore.getState().toggle('friend');
      } else if (e.code === 'KeyY') {
        e.preventDefault();
        useUIStore.getState().toggle('party');
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const renderMainBtn = (b: ActionBtn) => {
    const badge = b.key === 'stat' && statPoints > 0 ? statPoints
      : b.key === 'skill' && skillPoints > 0 ? skillPoints
      : b.key === 'friend' && friendBadge > 0 ? friendBadge
      : b.key === 'party' && partyBadge > 0 ? partyBadge
      : null;
    return (
      <button
        key={b.key}
        title={`${b.label} (${b.hot})`}
        style={ICON_BTN}
        onClick={() => {
          toggle(b.key);
          if (b.key === 'friend') useNotificationStore.getState().clearFriend();
          if (b.key === 'party') useNotificationStore.getState().clearParty();
        }}
      >
        <img src={b.icon} alt={b.label} style={{ width: 42, height: 42, imageRendering: 'pixelated' }} />
        <span style={NAME_LABEL}>{b.label} ({b.hot})</span>
        {badge !== null && <span style={BADGE}>{badge}</span>}
      </button>
    );
  };

  const half = Math.ceil(BUTTONS.length / 2);
  const row1 = BUTTONS.slice(0, half);
  const row2 = BUTTONS.slice(half);

  const ROW: React.CSSProperties = {
    display: 'flex',
    gap: 4,
    alignItems: 'center',
  };

  return (
    <div
      data-block-game-input="true"
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'absolute',
        right: 10,
        bottom: 10,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        padding: 4,
        alignItems: 'flex-end',
      }}
    >
      <div style={ROW}>{row1.map(renderMainBtn)}</div>
      <div style={ROW}>
        {row2.map(renderMainBtn)}
        <button
          title="Cài đặt"
          style={ICON_BTN}
          onClick={() => toggle('settings')}
        >
          <img src={`${ICON_BASE}icon_settings.png`} alt="Cài đặt" style={{ width: 42, height: 42, imageRendering: 'pixelated' }} />
          <span style={NAME_LABEL}>Cài đặt</span>
        </button>
        {isAdmin && (
          <button
            title="Admin"
            style={{ ...ICON_BTN, color: '#ff6b6b', borderColor: '#ff6b6b' }}
            onClick={() => toggle('admin')}
          >
            <img src={`${ICON_BASE}icon_admin.png`} alt="Admin" style={{ width: 42, height: 42, imageRendering: 'pixelated' }} />
            <span style={NAME_LABEL}>Admin</span>
          </button>
        )}
        <button
          title="Logout"
          style={{ ...ICON_BTN, color: '#ff9f9f', borderColor: '#7a3a3a' }}
          onClick={() => { disconnectRealtime(); clearAuth(); }}
        >
          <img src={`${ICON_BASE}icon_logout.png`} alt="Logout" style={{ width: 42, height: 42, imageRendering: 'pixelated' }} />
          <span style={NAME_LABEL}>Thoát</span>
        </button>
      </div>
    </div>
  );
}
